/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  Dimensions,
  TextInput,
  FlatList,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Platform,
  Linking,
  Share,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';

const { width } = Dimensions.get('window');

const Internships = () => {
  const navigation = useNavigation();
  const [isOnline, setIsOnline] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredInternships, setFilteredInternships] = useState([]);
  const [selectedField, setSelectedField] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedDuration, setSelectedDuration] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [savedInternships, setSavedInternships] = useState([]);
  const [appliedInternships, setAppliedInternships] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedInternship, setSelectedInternship] = useState(null);

  const searchTimeoutRef = useRef(null);
  const flatListRef = useRef(null);

  const fields = [
    { id: 'all', label: 'All Fields', icon: 'grid' },
    { id: 'software', label: 'Software', icon: 'code-braces' },
    { id: 'mechanical', label: 'Mechanical', icon: 'cog' },
    { id: 'civil', label: 'Civil', icon: 'bridge' },
    { id: 'electrical', label: 'Electrical', icon: 'flash' },
    { id: 'chemical', label: 'Chemical', icon: 'flask' },
    { id: 'aerospace', label: 'Aerospace', icon: 'airplane' },
    { id: 'biomedical', label: 'Biomedical', icon: 'heart-pulse' },
    { id: 'data', label: 'Data Science', icon: 'chart-bar' },
  ];

  const locations = [
    { id: 'all', label: 'All Locations' },
    { id: 'remote', label: 'Remote' },
    { id: 'uk', label: 'UK' },
    { id: 'usa', label: 'USA' },
    { id: 'germany', label: 'Germany' },
    { id: 'canada', label: 'Canada' },
    { id: 'australia', label: 'Australia' },
    { id: 'london', label: 'London' },
    { id: 'manchester', label: 'Manchester' },
  ];

  const durations = [
    { id: 'all', label: 'Any Duration' },
    { id: 'summer', label: 'Summer (3 months)' },
    { id: 'semester', label: 'Semester (4-6 months)' },
    { id: 'year', label: 'Year-long' },
    { id: 'flexible', label: 'Flexible' },
  ];

  const types = [
    { id: 'all', label: 'All Types' },
    { id: 'paid', label: 'Paid' },
    { id: 'unpaid', label: 'Unpaid' },
    { id: 'credit', label: 'For Credit' },
    { id: 'coop', label: 'Co-op' },
  ];

  const [internships, setInternships] = useState([
    {
      id: 'INT001',
      title: 'Software Engineering Intern',
      company: 'Google',
      logo: 'https://cdn-icons-png.flaticon.com/512/300/300221.png',
      location: 'London, UK',
      field: 'software',
      type: 'paid',
      duration: 'summer',
      salary: '£4,500/month',
      applicationDeadline: '2024-03-15',
      startDate: '2024-06-01',
      endDate: '2024-08-31',
      description: 'Join Google\'s engineering team for a summer internship. Work on cutting-edge projects in cloud computing and machine learning.',
      requirements: [
        'Currently pursuing a BS/MS in Computer Science',
        'Strong programming skills in Python/Java',
        'Experience with data structures and algorithms',
        'Knowledge of web technologies'
      ],
      responsibilities: [
        'Design and implement software solutions',
        'Collaborate with cross-functional teams',
        'Write clean, maintainable code',
        'Participate in code reviews'
      ],
      benefits: [
        'Competitive salary',
        'Housing stipend',
        'Mentorship program',
        'Networking events',
        'Potential return offer'
      ],
      applyLink: 'https://careers.google.com',
      contactEmail: 'internships@google.com',
      spots: 25,
      applications: 1250,
      featured: true,
      urgent: true,
    },
    {
      id: 'INT002',
      title: 'Mechanical Design Intern',
      company: 'Rolls-Royce',
      logo: 'https://cdn-icons-png.flaticon.com/512/841/841364.png',
      location: 'Derby, UK',
      field: 'mechanical',
      type: 'paid',
      duration: 'year',
      salary: '£2,800/month',
      applicationDeadline: '2024-04-30',
      startDate: '2024-09-01',
      endDate: '2025-08-31',
      description: 'Year-long placement in aerospace engineering. Work on propulsion systems and mechanical design.',
      requirements: [
        'Mechanical Engineering student',
        'CAD proficiency (SolidWorks/CATIA)',
        'Understanding of thermodynamics',
        'Strong analytical skills'
      ],
      responsibilities: [
        'Assist in design and analysis',
        'Create technical drawings',
        'Conduct simulations',
        'Prepare documentation'
      ],
      benefits: [
        'Industry experience',
        'Professional training',
        'Travel opportunities',
        'Company pension'
      ],
      applyLink: 'https://careers.rolls-royce.com',
      contactEmail: 'early.careers@rolls-royce.com',
      spots: 15,
      applications: 680,
      featured: true,
      urgent: false,
    },
    {
      id: 'INT003',
      title: 'Civil Engineering Intern',
      company: 'Arup',
      logo: 'https://cdn-icons-png.flaticon.com/512/3220/3220315.png',
      location: 'Manchester, UK',
      field: 'civil',
      type: 'paid',
      duration: 'semester',
      salary: '£2,500/month',
      applicationDeadline: '2024-03-31',
      startDate: '2024-09-01',
      endDate: '2025-02-28',
      description: 'Semester internship focusing on sustainable infrastructure projects.',
      requirements: [
        'Civil Engineering student',
        'Knowledge of structural analysis',
        'AutoCAD proficiency',
        'Interest in sustainability'
      ],
      responsibilities: [
        'Site inspections',
        'Structural calculations',
        'Project documentation',
        'Client meetings'
      ],
      benefits: [
        'Hands-on site experience',
        'Mentorship',
        'Networking',
        'Training courses'
      ],
      applyLink: 'https://www.arup.com/careers',
      contactEmail: 'graduates@arup.com',
      spots: 20,
      applications: 450,
      featured: false,
      urgent: true,
    },
    {
      id: 'INT004',
      title: 'Electrical Engineering Intern',
      company: 'Siemens',
      logo: 'https://cdn-icons-png.flaticon.com/512/5968/5968350.png',
      location: 'Berlin, Germany',
      field: 'electrical',
      type: 'paid',
      duration: 'summer',
      salary: '€3,200/month',
      applicationDeadline: '2024-04-15',
      startDate: '2024-07-01',
      endDate: '2024-09-30',
      description: 'Summer internship in renewable energy systems and smart grid technology.',
      requirements: [
        'Electrical Engineering student',
        'Circuit design knowledge',
        'Power systems understanding',
        'German language optional'
      ],
      responsibilities: [
        'Circuit design and testing',
        'Data analysis',
        'Report writing',
        'Team collaboration'
      ],
      benefits: [
        'International experience',
        'Language courses',
        'Housing assistance',
        'Travel reimbursement'
      ],
      applyLink: 'https://new.siemens.com/global/en/company/jobs.html',
      contactEmail: 'internships@siemens.com',
      spots: 30,
      applications: 920,
      featured: true,
      urgent: false,
    },
    {
      id: 'INT005',
      title: 'Data Science Intern',
      company: 'Amazon',
      logo: 'https://cdn-icons-png.flaticon.com/512/731/731985.png',
      location: 'Remote',
      field: 'data',
      type: 'paid',
      duration: 'flexible',
      salary: '$6,500/month',
      applicationDeadline: '2024-05-01',
      startDate: '2024-06-01',
      endDate: '2024-12-31',
      description: 'Remote internship in data analytics and machine learning for e-commerce.',
      requirements: [
        'Strong Python/R skills',
        'SQL proficiency',
        'Machine learning knowledge',
        'Statistical analysis'
      ],
      responsibilities: [
        'Data analysis and visualization',
        'Model development',
        'A/B testing',
        'Insight generation'
      ],
      benefits: [
        'Flexible hours',
        'Remote work',
        'Stock options',
        'Amazon perks'
      ],
      applyLink: 'https://www.amazon.jobs',
      contactEmail: 'student-programs@amazon.com',
      spots: 40,
      applications: 2100,
      featured: true,
      urgent: true,
    },
    {
      id: 'INT006',
      title: 'Chemical Engineering Intern',
      company: 'BP',
      logo: 'https://cdn-icons-png.flaticon.com/512/3208/3208726.png',
      location: 'Houston, USA',
      field: 'chemical',
      type: 'paid',
      duration: 'summer',
      salary: '$5,200/month',
      applicationDeadline: '2024-03-20',
      startDate: '2024-05-27',
      endDate: '2024-08-16',
      description: 'Summer internship in petroleum refining and process engineering.',
      requirements: [
        'Chemical Engineering student',
        'Process design knowledge',
        'Laboratory experience',
        'Safety awareness'
      ],
      responsibilities: [
        'Process optimization',
        'Safety analysis',
        'Data collection',
        'Technical reporting'
      ],
      benefits: [
        'Competitive pay',
        'Housing provided',
        'Field trips',
        'Mentorship'
      ],
      applyLink: 'https://www.bp.com/en/global/corporate/careers.html',
      contactEmail: 'early.careers@bp.com',
      spots: 18,
      applications: 760,
      featured: false,
      urgent: false,
    },
    {
      id: 'INT007',
      title: 'Aerospace Engineering Intern',
      company: 'Airbus',
      logo: 'https://cdn-icons-png.flaticon.com/512/809/809957.png',
      location: 'Toulouse, France',
      field: 'aerospace',
      type: 'paid',
      duration: 'semester',
      salary: '€2,800/month',
      applicationDeadline: '2024-04-10',
      startDate: '2024-09-01',
      endDate: '2025-02-28',
      description: 'Semester internship in aircraft design and aerodynamics.',
      requirements: [
        'Aerospace Engineering student',
        'Aerodynamics knowledge',
        'CAD experience',
        'French language preferred'
      ],
      responsibilities: [
        'Aerodynamic analysis',
        'Design support',
        'Testing coordination',
        'Documentation'
      ],
      benefits: [
        'French language course',
        'European travel',
        'Industry exposure',
        'Networking events'
      ],
      applyLink: 'https://www.airbus.com/en/careers',
      contactEmail: 'internships@airbus.com',
      spots: 22,
      applications: 890,
      featured: true,
      urgent: false,
    },
    {
      id: 'INT008',
      title: 'Biomedical Engineering Intern',
      company: 'Medtronic',
      logo: 'https://cdn-icons-png.flaticon.com/512/2785/2785352.png',
      location: 'Minneapolis, USA',
      field: 'biomedical',
      type: 'paid',
      duration: 'summer',
      salary: '$4,800/month',
      applicationDeadline: '2024-03-25',
      startDate: '2024-06-03',
      endDate: '2024-08-23',
      description: 'Summer internship in medical device development and testing.',
      requirements: [
        'Biomedical Engineering student',
        'Biology/physiology knowledge',
        'Lab experience',
        'Regulatory awareness'
      ],
      responsibilities: [
        'Device testing',
        'Protocol development',
        'Data analysis',
        'Quality assurance'
      ],
      benefits: [
        'Healthcare experience',
        'Professional development',
        'Networking',
        'Potential full-time offer'
      ],
      applyLink: 'https://www.medtronic.com/us-en/careers.html',
      contactEmail: 'campus.recruiting@medtronic.com',
      spots: 16,
      applications: 540,
      featured: false,
      urgent: true,
    },
  ]);

  useEffect(() => {
    loadInitialData();
    setupNetInfoListener();
  }, []);

  useEffect(() => {
    filterInternships();
  }, [searchQuery, selectedField, selectedLocation, selectedDuration, selectedType, internships]);

  const setupNetInfoListener = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
    });
    return unsubscribe;
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadDarkModePreference(),
        loadSavedInternships(),
        loadAppliedInternships(),
      ]);
      filterInternships();
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load internships');
    } finally {
      setLoading(false);
    }
  };

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadSavedInternships = async () => {
    try {
      const saved = await AsyncStorage.getItem('savedInternships');
      if (saved) {
        setSavedInternships(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved internships:', error);
    }
  };

  const loadAppliedInternships = async () => {
    try {
      const applied = await AsyncStorage.getItem('appliedInternships');
      if (applied) {
        setAppliedInternships(JSON.parse(applied));
      }
    } catch (error) {
      console.error('Error loading applied internships:', error);
    }
  };

  const refreshData = async () => {
    if (!isOnline) return;
    
    setIsRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      filterInternships();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const filterInternships = () => {
    let filtered = [...internships];
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(internship =>
        String(internship.title ?? '').toLowerCase().includes(query) ||
        String(internship.company ?? '').toLowerCase().includes(query) ||
        String(internship.description ?? '').toLowerCase().includes(query) ||
        String(internship.location ?? '').toLowerCase().includes(query)
      );
    }
    
    if (selectedField !== 'all') {
      filtered = filtered.filter(internship => internship.field === selectedField);
    }
    
    if (selectedLocation !== 'all') {
      filtered = filtered.filter(internship => {
        if (selectedLocation === 'remote') {
          return String(internship.location ?? '').toLowerCase().includes('remote');
        } else if (selectedLocation === 'uk') {
          return String(internship.location ?? '').toLowerCase().includes('uk') || 
                 String(internship.location ?? '').toLowerCase().includes('london') ||
                 String(internship.location ?? '').toLowerCase().includes('manchester') ||
                 String(internship.location ?? '').toLowerCase().includes('derby');
        }
        return String(internship.location ?? '').toLowerCase().includes(selectedLocation.toLowerCase());
      });
    }
    
    if (selectedDuration !== 'all') {
      filtered = filtered.filter(internship => internship.duration === selectedDuration);
    }
    
    if (selectedType !== 'all') {
      filtered = filtered.filter(internship => internship.type === selectedType);
    }
    
    setFilteredInternships(filtered);
  };

  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      filterInternships();
    }, 300);
  };

  const toggleSaveInternship = async (internshipId) => {
    try {
      let newSaved;
      if (savedInternships.includes(internshipId)) {
        newSaved = savedInternships.filter(id => id !== internshipId);
      } else {
        newSaved = [...savedInternships, internshipId];
      }
      
      setSavedInternships(newSaved);
      await AsyncStorage.setItem('savedInternships', JSON.stringify(newSaved));
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  const applyToInternship = async (internshipId) => {
    try {
      const newApplied = [...appliedInternships, internshipId];
      setAppliedInternships(newApplied);
      await AsyncStorage.setItem('appliedInternships', JSON.stringify(newApplied));
      
      Alert.alert(
        'Application Submitted',
        'Your application has been submitted successfully! You\'ll hear back from the company soon.',
        [{ text: 'OK' }]
      );
    } catch (error) {
      console.error('Error applying to internship:', error);
    }
  };

  const openApplicationLink = (link) => {
    Linking.openURL(link).catch(err => {
      console.error('Failed to open link:', err);
      Alert.alert('Error', 'Could not open application link');
    });
  };

  const shareInternship = async (internship) => {
    try {
      await Share.share({
        title: internship.title,
        message: `Check out this internship: ${internship.title} at ${internship.company}\n\nLocation: ${internship.location}\nDeadline: ${internship.applicationDeadline}\n\nApply here: ${internship.applyLink}`,
        url: internship.applyLink,
      });
    } catch (error) {
      console.error('Error sharing internship:', error);
    }
  };

  const calculateDaysLeft = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getFieldColor = (field) => {
    const colors = darkMode ? {
      software: ['#4c51bf', '#667eea'],
      mechanical: ['#dd6b20', '#ed8936'],
      civil: ['#2b6cb0', '#4299e1'],
      electrical: ['#d69e2e', '#ecc94b'],
      chemical: ['#38a169', '#68d391'],
      aerospace: ['#9f7aea', '#b794f4'],
      biomedical: ['#ed64a6', '#f687b3'],
      data: ['#4c51bf', '#667eea'],
      default: ['#718096', '#a0aec0'],
    } : {
      software: ['#6366f1', '#818cf8'],
      mechanical: ['#f97316', '#fb923c'],
      civil: ['#3b82f6', '#60a5fa'],
      electrical: ['#eab308', '#facc15'],
      chemical: ['#10b981', '#34d399'],
      aerospace: ['#8b5cf6', '#a78bfa'],
      biomedical: ['#ec4899', '#f472b6'],
      data: ['#6366f1', '#818cf8'],
      default: ['#6b7280', '#9ca3af'],
    };
    
    return colors[field] || colors.default;
  };

  const getFieldIcon = (field) => {
    const icons = {
      software: 'code-braces',
      mechanical: 'cog',
      civil: 'bridge',
      electrical: 'flash',
      chemical: 'flask',
      aerospace: 'airplane',
      biomedical: 'heart-pulse',
      data: 'chart-bar',
      default: 'briefcase',
    };
    
    return icons[field] || icons.default;
  };

  // NEW: Function to navigate to apply screen
  const navigateToApply = (internship) => {
    navigation.navigate('InternshipApply', { 
      internship,
      onApplySuccess: () => {
        // Callback after successful application
        applyToInternship(internship.id);
      }
    });
  };

  const renderHeader = () => (
    <LinearGradient
      colors={darkMode ? ['#2d3748', '#4a5568'] : ['#667eea', '#764ba2']}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={22} color="#ffffff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        <Text style={styles.headerTitle}>💼 Engineering Internships</Text>
        <Text style={styles.headerSubtitle}>
          {filteredInternships.length} opportunities
        </Text>
      </View>
      <TouchableOpacity 
        style={styles.viewModeButton}
        onPress={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
      >
        <MaterialCommunityIcons 
          name={viewMode === 'grid' ? 'view-list' : 'view-grid'} 
          size={22} 
          color="#ffffff" 
        />
      </TouchableOpacity>
    </LinearGradient>
  );

  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <MaterialCommunityIcons 
        name="magnify" 
        size={18} 
        color={darkMode ? "#a0aec0" : "#6b7280"} 
        style={styles.searchIcon}
      />
      <TextInput
        style={styles.searchInput}
        placeholder="Search internships, companies, locations..."
        value={searchQuery}
        onChangeText={handleSearch}
        placeholderTextColor={darkMode ? "#a0aec0" : "#6b7280"}
      />
      {searchQuery.length > 0 && (
        <TouchableOpacity onPress={() => setSearchQuery('')}>
          <MaterialCommunityIcons 
            name="close-circle" 
            size={18} 
            color={darkMode ? "#a0aec0" : "#6b7280"} 
          />
        </TouchableOpacity>
      )}
    </View>
  );

  const renderFilterChips = () => (
    <View style={styles.filterContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterScrollContent}
      >
        <TouchableOpacity
          style={styles.filterButton}
          onPress={() => setShowFilters(!showFilters)}
        >
          <MaterialCommunityIcons 
            name="filter-variant" 
            size={14} 
            color="#ffffff" 
          />
          <Text style={styles.filterButtonText}>Filters</Text>
        </TouchableOpacity>

        {selectedField !== 'all' && (
          <TouchableOpacity
            style={styles.activeFilterChip}
            onPress={() => setSelectedField('all')}
          >
            <Text style={styles.activeFilterText}>{selectedField}</Text>
            <MaterialCommunityIcons name="close" size={12} color="#ffffff" />
          </TouchableOpacity>
        )}

        {selectedLocation !== 'all' && (
          <TouchableOpacity
            style={styles.activeFilterChip}
            onPress={() => setSelectedLocation('all')}
          >
            <Text style={styles.activeFilterText}>{selectedLocation}</Text>
            <MaterialCommunityIcons name="close" size={12} color="#ffffff" />
          </TouchableOpacity>
        )}

        {selectedDuration !== 'all' && (
          <TouchableOpacity
            style={styles.activeFilterChip}
            onPress={() => setSelectedDuration('all')}
          >
            <Text style={styles.activeFilterText}>{selectedDuration}</Text>
            <MaterialCommunityIcons name="close" size={12} color="#ffffff" />
          </TouchableOpacity>
        )}
      </ScrollView>
    </View>
  );

  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowFilters(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.modalHeader}>
            <View style={styles.modalTitleContainer}>
              <MaterialCommunityIcons name="filter-variant" size={18} color={darkMode ? '#ffffff' : '#1f2937'} />
              <Text style={[styles.modalTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                Filter Internships
              </Text>
            </View>
            <TouchableOpacity onPress={() => setShowFilters(false)}>
              <MaterialCommunityIcons name="close" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.filterOptions}>
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Engineering Field
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.categoryOptions}>
                  {fields.map((field) => (
                    <TouchableOpacity
                      key={field.id}
                      style={[
                        styles.categoryChip,
                        selectedField === field.id && styles.categoryChipActive
                      ]}
                      onPress={() => setSelectedField(field.id)}
                    >
                      <MaterialCommunityIcons 
                        name={field.icon} 
                        size={14} 
                        color={selectedField === field.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                      />
                      <Text style={[
                        styles.categoryChipText,
                        selectedField === field.id && styles.categoryChipTextActive
                      ]}>
                        {field.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Location
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.locationOptions}>
                  {locations.map((location) => (
                    <TouchableOpacity
                      key={location.id}
                      style={[
                        styles.locationChip,
                        selectedLocation === location.id && styles.locationChipActive
                      ]}
                      onPress={() => setSelectedLocation(location.id)}
                    >
                      <Text style={[
                        styles.locationChipText,
                        selectedLocation === location.id && styles.locationChipTextActive
                      ]}>
                        {location.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Duration
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.durationOptions}>
                  {durations.map((duration) => (
                    <TouchableOpacity
                      key={duration.id}
                      style={[
                        styles.durationChip,
                        selectedDuration === duration.id && styles.durationChipActive
                      ]}
                      onPress={() => setSelectedDuration(duration.id)}
                    >
                      <Text style={[
                        styles.durationChipText,
                        selectedDuration === duration.id && styles.durationChipTextActive
                      ]}>
                        {duration.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Type
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.typeOptions}>
                  {types.map((type) => (
                    <TouchableOpacity
                      key={type.id}
                      style={[
                        styles.typeChip,
                        selectedType === type.id && styles.typeChipActive
                      ]}
                      onPress={() => setSelectedType(type.id)}
                    >
                      <Text style={[
                        styles.typeChipText,
                        selectedType === type.id && styles.typeChipTextActive
                      ]}>
                        {type.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
          </ScrollView>

          <View style={styles.filterActions}>
            <TouchableOpacity 
              style={styles.resetButton}
              onPress={() => {
                setSelectedField('all');
                setSelectedLocation('all');
                setSelectedDuration('all');
                setSelectedType('all');
              }}
            >
              <Text style={styles.resetButtonText}>Reset All</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.applyButton}
              onPress={() => setShowFilters(false)}
            >
              <Text style={styles.applyButtonText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderInternshipGrid = ({ item, index }) => {
    const isSaved = savedInternships.includes(item.id);
    const isApplied = appliedInternships.includes(item.id);
    const daysLeft = calculateDaysLeft(item.applicationDeadline);
    
    return (
      <TouchableOpacity
        style={styles.internshipCardGrid}
        onPress={() => {
          setSelectedInternship(item);
          setDetailModalVisible(true);
        }}
        activeOpacity={0.8}
      >
        <View style={styles.internshipHeaderGrid}>
          <LinearGradient
            colors={getFieldColor(item.field)}
            style={styles.internshipFieldGrid}
          >
            <MaterialCommunityIcons name={getFieldIcon(item.field)} size={16} color="#ffffff" />
            <Text style={styles.internshipFieldText}>{String(item.field ?? '').toUpperCase()}</Text>
          </LinearGradient>
          
          <TouchableOpacity 
            style={styles.saveButtonGrid}
            onPress={() => toggleSaveInternship(item.id)}
          >
            <MaterialCommunityIcons 
              name={isSaved ? "bookmark" : "bookmark-outline"} 
              size={18} 
              color={isSaved ? "#667eea" : (darkMode ? "#a0aec0" : "#6b7280")} 
            />
          </TouchableOpacity>
        </View>

        <View style={styles.internshipContentGrid}>
          <Text style={styles.internshipTitleGrid} numberOfLines={2}>
            {item.title}
          </Text>
          
          <View style={styles.companyContainer}>
            <View style={styles.companyLogo}>
              <MaterialCommunityIcons name="office-building" size={14} color={darkMode ? "#a0aec0" : "#6b7280"} />
            </View>
            <Text style={styles.companyText} numberOfLines={1}>
              {item.company}
            </Text>
          </View>

          <View style={styles.internshipMetaGrid}>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="map-marker" size={10} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.metaText}>{item.location}</Text>
            </View>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="calendar" size={10} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.metaText}>{item.duration}</Text>
            </View>
          </View>

          <View style={styles.internshipStatsGrid}>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="currency-usd" size={10} color="#10b981" />
              <Text style={styles.statText}>{item.salary}</Text>
            </View>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="clock" size={10} color="#f59e0b" />
              <Text style={styles.statText}>{daysLeft}d left</Text>
            </View>
          </View>

          {item.featured && (
            <View style={styles.featuredBadge}>
              <MaterialCommunityIcons name="star" size={10} color="#ffffff" />
              <Text style={styles.featuredText}>Featured</Text>
            </View>
          )}

          {item.urgent && (
            <View style={styles.urgentBadge}>
              <MaterialCommunityIcons name="alert" size={10} color="#ffffff" />
              <Text style={styles.urgentText}>Urgent</Text>
            </View>
          )}
        </View>

        <View style={styles.internshipActionsGrid}>
          {isApplied ? (
            <TouchableOpacity 
              style={styles.appliedButton}
              disabled={true}
            >
              <MaterialCommunityIcons name="check-circle" size={14} color="#ffffff" />
              <Text style={styles.appliedText}>Applied</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity 
              style={styles.applyButtonSmall}
              onPress={() => navigateToApply(item)}
            >
              <Text style={styles.applyTextSmall}>Apply Now</Text>
              <MaterialCommunityIcons name="arrow-right" size={12} color="#ffffff" />
            </TouchableOpacity>
          )}
          
          <TouchableOpacity 
            style={styles.detailsButton}
            onPress={() => {
              setSelectedInternship(item);
              setDetailModalVisible(true);
            }}
          >
            <Text style={styles.detailsText}>Details</Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  };

  const renderInternshipList = ({ item, index }) => {
    const isSaved = savedInternships.includes(item.id);
    const isApplied = appliedInternships.includes(item.id);
    const daysLeft = calculateDaysLeft(item.applicationDeadline);
    
    return (
      <TouchableOpacity
        style={styles.internshipCardList}
        onPress={() => {
          setSelectedInternship(item);
          setDetailModalVisible(true);
        }}
        activeOpacity={0.8}
      >
        <LinearGradient
          colors={getFieldColor(item.field)}
          style={styles.internshipFieldList}
        >
          <MaterialCommunityIcons name={getFieldIcon(item.field)} size={18} color="#ffffff" />
        </LinearGradient>

        <View style={styles.internshipContentList}>
          <View style={styles.internshipHeaderList}>
            <View style={styles.titleContainer}>
              <Text style={styles.internshipTitleList} numberOfLines={1}>
                {item.title}
              </Text>
              <Text style={styles.companyTextList}>{item.company}</Text>
            </View>
            <TouchableOpacity 
              style={styles.saveButtonList}
              onPress={() => toggleSaveInternship(item.id)}
            >
              <MaterialCommunityIcons 
                name={isSaved ? "bookmark" : "bookmark-outline"} 
                size={18} 
                color={isSaved ? "#667eea" : (darkMode ? "#a0aec0" : "#6b7280")} 
              />
            </TouchableOpacity>
          </View>

          <View style={styles.internshipDetailsList}>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="map-marker" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.detailText}>{item.location}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="calendar" size={12} color={darkMode ? "#a0aec0" : "#6b7280"} />
              <Text style={styles.detailText}>{item.duration}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="currency-usd" size={12} color="#10b981" />
              <Text style={styles.detailText}>{item.salary}</Text>
            </View>
          </View>

          <View style={styles.internshipFooterList}>
            <View style={styles.deadlineContainer}>
              <MaterialCommunityIcons name="clock-outline" size={12} color="#ef4444" />
              <Text style={styles.deadlineText}>{daysLeft} days left</Text>
            </View>
            
            <View style={styles.internshipActionsList}>
              {isApplied ? (
                <TouchableOpacity 
                  style={styles.appliedButtonList}
                  disabled={true}
                >
                  <MaterialCommunityIcons name="check-circle" size={12} color="#ffffff" />
                  <Text style={styles.appliedTextList}>Applied</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity 
                  style={styles.applyButtonList}
                  onPress={() => navigateToApply(item)}
                >
                  <Text style={styles.applyTextList}>Quick Apply</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderDetailModal = () => (
    <Modal
      visible={detailModalVisible}
      animationType="slide"
      onRequestClose={() => setDetailModalVisible(false)}
    >
      <SafeAreaView style={[styles.detailModalContainer, { backgroundColor: darkMode ? '#1a202c' : '#ffffff' }]}>
        {selectedInternship && (
          <>
            <LinearGradient
              colors={getFieldColor(selectedInternship.field)}
              style={styles.detailHeader}
            >
              <TouchableOpacity 
                style={styles.detailBackButton}
                onPress={() => setDetailModalVisible(false)}
              >
                <MaterialCommunityIcons name="close" size={22} color="#ffffff" />
              </TouchableOpacity>
              <View style={styles.detailHeaderCenter}>
                <Text style={styles.detailTitle} numberOfLines={2}>
                  {selectedInternship.title}
                </Text>
                <Text style={styles.detailSubtitle}>
                  {selectedInternship.company} • {selectedInternship.location}
                </Text>
              </View>
              <TouchableOpacity 
                style={styles.detailShareButton}
                onPress={() => shareInternship(selectedInternship)}
              >
                <MaterialCommunityIcons name="share-variant" size={22} color="#ffffff" />
              </TouchableOpacity>
            </LinearGradient>

            <ScrollView style={styles.detailContent}>
              <View style={styles.detailOverview}>
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="calendar-range" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Duration
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedInternship.duration}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="currency-usd" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Salary
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedInternship.salary}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="clock-outline" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Deadline
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedInternship.applicationDeadline}
                  </Text>
                </View>
                
                <View style={styles.overviewItem}>
                  <MaterialCommunityIcons name="account-group" size={16} color={darkMode ? "#a0aec0" : "#6b7280"} />
                  <Text style={[styles.overviewLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Spots
                  </Text>
                  <Text style={[styles.overviewValue, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                    {selectedInternship.spots} available
                  </Text>
                </View>
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Description
                </Text>
                <Text style={[styles.sectionContent, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                  {selectedInternship.description}
                </Text>
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Requirements
                </Text>
                {selectedInternship.requirements.map((req, index) => (
                  <View key={index} style={styles.listItem}>
                    <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
                    <Text style={[styles.listText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {req}
                    </Text>
                  </View>
                ))}
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Responsibilities
                </Text>
                {selectedInternship.responsibilities.map((resp, index) => (
                  <View key={index} style={styles.listItem}>
                    <MaterialCommunityIcons name="arrow-right-circle" size={14} color="#667eea" />
                    <Text style={[styles.listText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {resp}
                    </Text>
                  </View>
                ))}
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Benefits
                </Text>
                {selectedInternship.benefits.map((benefit, index) => (
                  <View key={index} style={styles.listItem}>
                    <MaterialCommunityIcons name="gift" size={14} color="#f59e0b" />
                    <Text style={[styles.listText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {benefit}
                    </Text>
                  </View>
                ))}
              </View>

              <View style={styles.detailSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                  Application Statistics
                </Text>
                <View style={styles.statsContainer}>
                  <View style={styles.statItemDetail}>
                    <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                      {selectedInternship.applications}
                    </Text>
                    <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Total Applications
                    </Text>
                  </View>
                  <View style={styles.statItemDetail}>
                    <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                      {selectedInternship.spots}
                    </Text>
                    <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Available Spots
                    </Text>
                  </View>
                  <View style={styles.statItemDetail}>
                    <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                      {Math.round((selectedInternship.applications / selectedInternship.spots) * 100)}:1
                    </Text>
                    <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Competition Ratio
                    </Text>
                  </View>
                </View>
              </View>
            </ScrollView>

            <View style={[styles.detailFooter, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
              <TouchableOpacity 
                style={styles.saveDetailButton}
                onPress={() => toggleSaveInternship(selectedInternship.id)}
              >
                <MaterialCommunityIcons 
                  name={savedInternships.includes(selectedInternship.id) ? "bookmark" : "bookmark-outline"} 
                  size={20} 
                  color={darkMode ? "#a0aec0" : "#6b7280"} 
                />
                <Text style={[styles.saveDetailText, { color: darkMode ? "#a0aec0" : "#6b7280" }]}>
                  {savedInternships.includes(selectedInternship.id) ? 'Saved' : 'Save'}
                </Text>
              </TouchableOpacity>
              
              {appliedInternships.includes(selectedInternship.id) ? (
                <TouchableOpacity 
                  style={styles.appliedDetailButton}
                  disabled={true}
                >
                  <MaterialCommunityIcons name="check-circle" size={20} color="#ffffff" />
                  <Text style={styles.appliedDetailText}>Already Applied</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity 
                  style={styles.applyDetailButton}
                  onPress={() => navigateToApply(selectedInternship)}
                >
                  <Text style={styles.applyDetailText}>Apply Now</Text>
                  <MaterialCommunityIcons name="arrow-right" size={18} color="#ffffff" />
                </TouchableOpacity>
              )}
            </View>
          </>
        )}
      </SafeAreaView>
    </Modal>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons 
        name="briefcase-search" 
        size={48} 
        color={darkMode ? "#4a5568" : "#d1d5db"} 
      />
      <Text style={[styles.emptyStateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        No Internships Found
      </Text>
      <Text style={[styles.emptyStateText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        Try adjusting your filters or check back later for new opportunities
      </Text>
      <TouchableOpacity 
        style={styles.resetFiltersButton}
        onPress={() => {
          setSearchQuery('');
          setSelectedField('all');
          setSelectedLocation('all');
          setSelectedDuration('all');
          setSelectedType('all');
        }}
      >
        <Text style={styles.resetFiltersText}>Reset All Filters</Text>
      </TouchableOpacity>
    </View>
  );

  const renderHeaderComponent = () => (
    <>
      {!isOnline && (
        <View style={styles.offlineBanner}>
          <MaterialCommunityIcons name="wifi-off" size={14} color="#ffffff" />
          <Text style={styles.offlineText}>Offline Mode - Showing cached data</Text>
        </View>
      )}

      <View style={styles.statsOverview}>
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="briefcase" size={20} color="#667eea" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {internships.length}
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Total Internships
          </Text>
        </View>
        
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {appliedInternships.length}
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Applied
          </Text>
        </View>
        
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="bookmark" size={20} color="#f59e0b" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {savedInternships.length}
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Saved
          </Text>
        </View>
        
        <View style={[styles.statCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <MaterialCommunityIcons name="star" size={20} color="#8b5cf6" />
          <Text style={[styles.statNumber, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            {internships.filter(i => i.featured).length}
          </Text>
          <Text style={[styles.statLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Featured
          </Text>
        </View>
      </View>

      <View style={styles.contentContainer}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            Internship Opportunities ({filteredInternships.length})
          </Text>
          <TouchableOpacity onPress={() => setShowFilters(true)}>
            <Text style={styles.filterLink}>Filter & Sort</Text>
          </TouchableOpacity>
        </View>
      </View>
    </>
  );

  if (loading) {
    return (
      <SafeAreaView style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
        <ActivityIndicator size="large" color="#667eea" />
        <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#6b7280' }]}>
          Loading internships...
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
      
      {renderHeader()}
      {renderSearchBar()}
      {renderFilterChips()}
      
      <FlatList
        ref={flatListRef}
        data={filteredInternships}
        key={viewMode === 'grid' ? 'grid' : 'list'}
        keyExtractor={(item) => item.id}
        numColumns={viewMode === 'grid' ? 2 : 1}
        contentContainerStyle={styles.scrollContent}
        ListHeaderComponent={renderHeaderComponent}
        ListEmptyComponent={renderEmptyState}
        renderItem={viewMode === 'grid' ? renderInternshipGrid : renderInternshipList}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={refreshData}
            colors={['#667eea']}
            tintColor="#667eea"
          />
        }
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
      />

      {renderFiltersModal()}
      {renderDetailModal()}

      <TouchableOpacity 
        style={styles.fab}
        onPress={() => navigation.navigate('SavedInternships', { savedInternships })}
      >
        <LinearGradient
          colors={['#667eea', '#764ba2']}
          style={styles.fabGradient}
        >
          <MaterialCommunityIcons name="bookmark" size={20} color="#ffffff" />
          {savedInternships.length > 0 && (
            <View style={styles.fabBadge}>
              <Text style={styles.fabBadgeText}>{savedInternships.length}</Text>
            </View>
          )}
        </LinearGradient>
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 6,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  viewModeButton: {
    padding: 6,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    marginHorizontal: 16,
    marginVertical: 8,
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 40,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 14,
    color: '#ffffff',
  },
  filterContainer: {
    height: 32,
    marginBottom: 4,
  },
  filterScrollContent: {
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 6,
    height: 24,
  },
  filterButtonText: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '500',
    marginLeft: 4,
  },
  activeFilterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#8b5cf6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 6,
    height: 24,
  },
  activeFilterText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '500',
    marginRight: 3,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 16,
  },
  offlineBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#6b7280',
    paddingVertical: 6,
    marginHorizontal: 16,
    borderRadius: 6,
    marginTop: 4,
    marginBottom: 8,
  },
  offlineText: {
    color: '#ffffff',
    fontSize: 11,
    marginLeft: 6,
  },
  statsOverview: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 16,
    marginBottom: 12,
    gap: 8,
  },
  statCard: {
    flex: 1,
    minWidth: '45%',
    alignItems: 'center',
    padding: 10,
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  statNumber: {
    fontSize: 16,
    fontWeight: 'bold',
    marginVertical: 4,
  },
  statLabel: {
    fontSize: 10,
  },
  contentContainer: {
    paddingHorizontal: 16,
    marginBottom: 8,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  filterLink: {
    fontSize: 12,
    color: '#667eea',
    fontWeight: '600',
  },
  internshipCardGrid: {
    flex: 1,
    margin: 6,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
    minHeight: 180,
  },
  internshipHeaderGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingTop: 10,
  },
  internshipFieldGrid: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  internshipFieldText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#ffffff',
    marginLeft: 4,
  },
  saveButtonGrid: {
    padding: 2,
  },
  internshipContentGrid: {
    padding: 10,
    flex: 1,
  },
  internshipTitleGrid: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 6,
    lineHeight: 16,
  },
  companyContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  companyLogo: {
    width: 20,
    height: 20,
    borderRadius: 4,
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 6,
  },
  companyText: {
    fontSize: 10,
    color: '#6b7280',
    flex: 1,
  },
  internshipMetaGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaText: {
    fontSize: 9,
    color: '#6b7280',
    marginLeft: 3,
  },
  internshipStatsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 4,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#374151',
    marginLeft: 3,
  },
  featuredBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f59e0b',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  featuredText: {
    fontSize: 8,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: 2,
  },
  urgentBadge: {
    position: 'absolute',
    top: 30,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ef4444',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  urgentText: {
    fontSize: 8,
    color: '#ffffff',
    fontWeight: 'bold',
    marginLeft: 2,
  },
  internshipActionsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  appliedButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    flex: 1,
    justifyContent: 'center',
  },
  appliedText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '600',
    marginLeft: 4,
  },
  applyButtonSmall: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    flex: 1,
    justifyContent: 'center',
  },
  applyTextSmall: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '600',
    marginRight: 4,
  },
  detailsButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginLeft: 8,
  },
  detailsText: {
    fontSize: 10,
    color: '#6b7280',
    fontWeight: '500',
  },
  internshipCardList: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    marginBottom: 8,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
    marginHorizontal: 16,
    minHeight: 100,
  },
  internshipFieldList: {
    width: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  internshipContentList: {
    flex: 1,
    padding: 12,
  },
  internshipHeaderList: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  titleContainer: {
    flex: 1,
    marginRight: 8,
  },
  internshipTitleList: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 4,
  },
  companyTextList: {
    fontSize: 11,
    color: '#6b7280',
  },
  saveButtonList: {
    padding: 2,
  },
  internshipDetailsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 8,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailText: {
    fontSize: 11,
    color: '#6b7280',
    marginLeft: 4,
  },
  internshipFooterList: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  deadlineContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deadlineText: {
    fontSize: 10,
    color: '#ef4444',
    fontWeight: '600',
    marginLeft: 4,
  },
  internshipActionsList: {
    flexDirection: 'row',
    gap: 8,
  },
  appliedButtonList: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  appliedTextList: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '600',
    marginLeft: 4,
  },
  applyButtonList: {
    backgroundColor: '#667eea',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  applyTextList: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 16,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 12,
  },
  emptyStateText: {
    fontSize: 13,
    marginTop: 6,
    textAlign: 'center',
  },
  resetFiltersButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
    marginTop: 12,
  },
  resetFiltersText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '70%',
    paddingBottom: Platform.OS === 'ios' ? 34 : 16,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  filterOptions: {
    paddingHorizontal: 14,
    paddingTop: 10,
  },
  filterSection: {
    marginBottom: 16,
  },
  filterSectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
  },
  categoryOptions: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  categoryChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  categoryChipActive: {
    backgroundColor: '#667eea',
  },
  categoryChipText: {
    fontSize: 11,
    color: '#6b7280',
    marginLeft: 4,
  },
  categoryChipTextActive: {
    color: '#ffffff',
  },
  locationOptions: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  locationChip: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  locationChipActive: {
    backgroundColor: '#667eea',
  },
  locationChipText: {
    fontSize: 11,
    color: '#6b7280',
  },
  locationChipTextActive: {
    color: '#ffffff',
  },
  durationOptions: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  durationChip: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  durationChipActive: {
    backgroundColor: '#667eea',
  },
  durationChipText: {
    fontSize: 11,
    color: '#6b7280',
  },
  durationChipTextActive: {
    color: '#ffffff',
  },
  typeOptions: {
    flexDirection: 'row',
    gap: 6,
    paddingVertical: 2,
  },
  typeChip: {
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
  },
  typeChipActive: {
    backgroundColor: '#667eea',
  },
  typeChipText: {
    fontSize: 11,
    color: '#6b7280',
  },
  typeChipTextActive: {
    color: '#ffffff',
  },
  filterActions: {
    flexDirection: 'row',
    paddingHorizontal: 14,
    paddingTop: 12,
    gap: 8,
  },
  resetButton: {
    flex: 1,
    backgroundColor: '#e5e7eb',
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  applyButton: {
    flex: 1,
    backgroundColor: '#667eea',
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  resetButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6b7280',
  },
  applyButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  detailModalContainer: {
    flex: 1,
  },
  detailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  detailBackButton: {
    padding: 6,
  },
  detailHeaderCenter: {
    flex: 1,
    marginHorizontal: 10,
  },
  detailTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  detailSubtitle: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  detailShareButton: {
    padding: 6,
  },
  detailContent: {
    flex: 1,
    padding: 16,
  },
  detailOverview: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: '#f3f4f6',
    padding: 12,
    borderRadius: 10,
    marginBottom: 16,
    gap: 12,
  },
  overviewItem: {
    width: '48%',
  },
  overviewLabel: {
    fontSize: 11,
    marginTop: 4,
  },
  overviewValue: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 2,
  },
  detailSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  sectionContent: {
    fontSize: 14,
    lineHeight: 20,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  listText: {
    fontSize: 13,
    lineHeight: 18,
    marginLeft: 8,
    flex: 1,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#f3f4f6',
    padding: 12,
    borderRadius: 10,
  },
  statItemDetail: {
    alignItems: 'center',
    flex: 1,
  },
  statNumber: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  statLabel: {
    fontSize: 10,
    marginTop: 4,
  },
  detailFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  saveDetailButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginRight: 12,
  },
  saveDetailText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  appliedDetailButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#10b981',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    justifyContent: 'center',
  },
  appliedDetailText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  applyDetailButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    flex: 1,
    justifyContent: 'center',
  },
  applyDetailText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginRight: 6,
  },
  fab: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 6,
  },
  fabGradient: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  fabBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#ef4444',
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fabBadgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
});

export default Internships;